/**
 * Small pieces the central control's screens share: the state pill, the type
 * chip, the usage meter, and the plain-Arabic summary of an audit row.
 */

import { Icon } from '../../../components/common';
import { STATE_ICONS, STATE_LABELS, STATE_TONES } from '../../../domain/serviceControl';
import { useI18n } from '../../../i18n/I18nProvider';

export const TYPE_LABELS = Object.freeze({ key: 'مفتاح', limit: 'مفتاح + حد', policy: 'سياسة', follow: 'تابعة', core: 'أساسية' });
export const TYPE_ICONS = Object.freeze({ key: 'toggle-left', limit: 'gauge', policy: 'scroll-text', follow: 'link', core: 'lock' });

/** The display state of a resolved control: draining wins over its target. */
export const displayState = (entry) => (entry && entry.draining ? 'draining' : (entry && entry.state) || 'open');

export function StatePill({ state, className = '' }) {
  const { tr } = useI18n();
  const tone = STATE_TONES[state] || 'slate';
  return (
    <span className={`control-pill tone-${tone} ${state === 'draining' ? 'control-pill--pulse' : ''} ${className}`} data-state={state}>
      <Icon name={STATE_ICONS[state] || 'circle'} size={12} />
      {tr(STATE_LABELS[state] || state)}
    </span>
  );
}

export function TypeChip({ type }) {
  return (
    <span className={`control-type control-type--${type}`}>
      <Icon name={TYPE_ICONS[type] || 'circle'} size={11} />
      {TYPE_LABELS[type] || type}
    </span>
  );
}

/**
 * Used against limit. Amber from 80% (the plan's notice), red at 100%; no
 * limit at all reads «غير محدود».
 */
export function Meter({ label, unit, used = 0, limit = null, className = '' }) {
  const { tr } = useI18n();
  if (limit === null || limit === undefined) {
    return (
      <div className={`control-meter ${className}`}>
        <div className="control-meter__head">
          <span>{label}</span>
          <span className="text-emerald-300">
            {used ? `${used} ${unit || ''} · ` : ''}
            {tr('غير محدود')}
          </span>
        </div>
      </div>
    );
  }
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
  const level = percent >= 100 ? 'full' : percent >= 80 ? 'warn' : 'ok';
  return (
    <div className={`control-meter control-meter--${level} ${className}`} data-level={level}>
      <div className="control-meter__head">
        <span>{label}</span>
        <span className="font-mono" dir="ltr">
          {used} / {limit} {unit ? <span dir="rtl">{unit}</span> : null}
        </span>
      </div>
      <div className="control-meter__track" role="meter" aria-valuenow={used} aria-valuemin={0} aria-valuemax={limit} aria-label={label}>
        <span className="control-meter__fill" style={{ '--to': `${percent}%` }} />
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  set: 'تعديل',
  clear: 'رجوع للباقة',
  plan: 'تغيير الباقة',
  'plan-edit': 'تعديل الباقة',
  expire: 'انتهاء تجربة',
  drained: 'اكتمال القفل',
};

export const actionLabel = (action) => ACTION_LABELS[action] || action;

export function formatWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  return `${d.toLocaleDateString('ar-EG', { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}`;
}
